import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Truck, ClipboardList, CheckCircle2, Circle, MapPin, Trash2, Filter } from "lucide-react";
import { PrintButton, PrintHeader } from "../components/Print";
import { formatDate, roleLabel } from "../lib/format";
import TaskFormDialog from "../components/TaskFormDialog";

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export default function DeliveriesPage() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(addDays(today(), 30));
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("open"); // open | all | done
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const params = { start, end };
    if (assigneeFilter) params.assignee_id = assigneeFilter;
    const { data } = await api.get("/tasks", { params });
    setTasks(data);
  };

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end, assigneeFilter]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return tasks;
    if (statusFilter === "done") return tasks.filter((t) => t.status === "done");
    return tasks.filter((t) => t.status !== "done");
  }, [tasks, statusFilter]);

  // Group by date
  const grouped = useMemo(() => {
    const g = new Map();
    for (const t of filtered) {
      const key = t.date;
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(t);
    }
    return Array.from(g.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const toggle = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: t.status === "done" ? "pending" : "done" });
      load();
    } catch { toast.error("Update failed"); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete delivery "${t.title}"?`)) return;
    await api.delete(`/tasks/${t.id}`);
    toast.success("Delivery removed");
    load();
  };

  return (
    <div className="print-container">
      <PageHeader
        eyebrow="Dispatch"
        title="Deliveries"
        description="Every scheduled delivery — janitorial, school supplies, project material — assigned to a teammate for a specific day."
        testId="deliveries-header"
        actions={
          <div className="flex items-center gap-2 no-print">
            <PrintButton testId="deliveries-print-btn" label="Print" />
            <Button
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              data-testid="new-delivery-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              <Plus size={16} className="mr-1.5" /> New Delivery
            </Button>
          </div>
        }
      />

      <div className="p-4 md:p-8 space-y-6">
        <PrintHeader
          title="Deliveries Schedule"
          subtitle={`${formatDate(start)} → ${formatDate(end)}${assigneeFilter ? ` · ${users.find((u) => u.id === assigneeFilter)?.name || ""}` : ""}`}
        />

        <div className="bg-white border border-slate-200 rounded-md p-4 no-print flex flex-wrap gap-4 items-end">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="deliveries-from" className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="deliveries-to" className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Assignee</Label>
            <Select value={assigneeFilter || "__all__"} onValueChange={(v) => setAssigneeFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="deliveries-assignee-filter" className="mt-1 rounded-sm w-48">
                <SelectValue placeholder="Everyone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Everyone</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="deliveries-status-filter" className="mt-1 rounded-sm w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open only</SelectItem>
                <SelectItem value="done">Completed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto text-xs font-mono text-slate-500 inline-flex items-center gap-1">
            <Filter size={12} /> {filtered.length} deliveries · {grouped.length} day{grouped.length === 1 ? "" : "s"}
          </div>
        </div>

        {grouped.length === 0 ? (
          <div className="border border-dashed border-slate-300 bg-white rounded-md p-12 text-center">
            <Truck size={40} className="mx-auto text-slate-400" />
            <div className="mt-3 font-display text-xl font-bold text-slate-900">No deliveries in this range</div>
            <p className="mt-1 text-sm text-slate-600">Adjust the date range or add a new delivery.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map(([date, items]) => (
              <div key={date} className="bg-white border border-slate-200 rounded-md shadow-sm print-block overflow-x-auto">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      {new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long" })}
                    </div>
                    <div className="font-display text-xl font-black tracking-tighter text-slate-900">{formatDate(date)}</div>
                  </div>
                  <div className="text-xs font-mono text-slate-500">{items.length} delivery{items.length === 1 ? "" : "ies"}</div>
                </div>
                <table className="w-full text-sm min-w-[720px]" data-testid={`deliveries-day-${date}`}>
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      <th className="p-3 w-8"></th>
                      <th className="p-3">Assignee</th>
                      <th className="p-3">Delivery</th>
                      <th className="p-3 text-right">Qty</th>
                      <th className="p-3">Where</th>
                      <th className="p-3">Project</th>
                      <th className="p-3 w-24 no-print"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((t) => {
                      const done = t.status === "done";
                      return (
                        <tr key={t.id} className="border-b border-slate-200 last:border-b-0" data-testid={`delivery-row-${t.id}`}>
                          <td className="p-3">
                            <button onClick={() => toggle(t)} className="no-print" aria-label={done ? "Mark pending" : "Mark done"} data-testid={`delivery-toggle-${t.id}`}>
                              {done ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Circle size={16} className="text-slate-400" />}
                            </button>
                            <span className="print-only" style={{ fontSize: 10 }}>{done ? "[✓]" : "[ ]"}</span>
                          </td>
                          <td className="p-3 text-slate-900">
                            <div className="font-semibold">{t.assignee_name || <span className="text-slate-400">Unassigned</span>}</div>
                            {t.assignee_role && <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{roleLabel(t.assignee_role)}</div>}
                          </td>
                          <td className="p-3">
                            <div className={`font-semibold ${done ? "line-through text-slate-500" : "text-slate-900"}`}>{t.title}</div>
                            {t.description && <div className="text-xs text-slate-600">{t.description}</div>}
                          </td>
                          <td className="p-3 text-right font-mono">{t.quantity != null ? t.quantity : "—"}</td>
                          <td className="p-3 text-slate-700 text-xs">
                            {t.location ? (
                              <span className="inline-flex items-center gap-1"><MapPin size={11} /> {t.location}</span>
                            ) : "—"}
                          </td>
                          <td className="p-3 text-xs">
                            {t.project_name ? (
                              <button onClick={() => navigate(`/projects/${t.project_id}`)} className="text-blue-800 font-semibold hover:underline no-print">
                                {t.project_name}
                              </button>
                            ) : "—"}
                            <span className="print-only">{t.project_name}</span>
                          </td>
                          <td className="p-3 text-right no-print">
                            <button onClick={() => { setEditing(t); setDialogOpen(true); }} className="text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-blue-800 mr-2" data-testid={`delivery-edit-${t.id}`}>Edit</button>
                            <button onClick={() => remove(t)} className="text-slate-400 hover:text-red-600" aria-label="Delete" data-testid={`delivery-delete-${t.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      <TaskFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        task={editing}
        defaultDate={today()}
        onSuccess={load}
      />
    </div>
  );
}
